import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import WizardStepper from '../components/ui/WizardStepper';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import Spinner from '../components/ui/Spinner';
import { AnimatePresence, motion } from 'framer-motion';
import './DocumentsForm.css';
import { AxiosError } from 'axios';

interface Document {
  id_docs: number;
  name: string;
  url: string;
  upload_date: string;
  id_user: number;
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

const documentTypes = [
  'Certidão de Nascimento - Frente',
  'Certidão de Nascimento - Verso',
  'Comprovante de Escolaridade',
  'Comprovante de Residência',
  'Contracheque/Holerite',
  'CPF',
  'Declaração de Acúmulo de Cargo',
  'Documentação do Declarante',
  'DOCUMENTOS NECESSÁRIO CENSO',
  'DECLARACAO-DE-RESIDENCIA',
  'DECLARACAO-DE-NAO-ATIVIDADE-REMUNERADA-APOSENTADO',
  'DECLARACAO-DE-ACUMULO-DE-CARGOBENEFICIO',
  'DECLARACAO-DE-SEPARACAO-DE-FATO',
  'DECLARACAO-DE-NAO-ATIVIDADE-REMUNERADA-PENSIONISTA',
  'DECLARACAO_DE_ESTADO_CIVIL',
  'LISTA-DE-DOCUMENTOS-CENSO-RIO-DE-JANEIRO-ATIVOS',
  'LISTA-DE-DOCUMENTOS-CENSO-RIO-DE-JANEIRO-INATIVOS',
  'LISTA-DE-DOCUMENTOS-CENSO-RIO-DE-JANEIRO-PENSIONISTAS',
];

const DocumentsForm: React.FC = () => {
  const { token, id_user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [previewUrls, setPreviewUrls] = useState<{ [key: string]: string }>({});
  const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dragOverType, setDragOverType] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !id_user) {
      return;
    }

    const fetchDocuments = async () => {
      try {
        const response = await api.get(`/docs/user/${id_user}`);
        if (response.data.message === 'User documents retrieved') {
          setDocuments(response.data.data || []);
        }
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao buscar documentos:', error.response?.status, error.response?.data);
        setErrorMessage(error.response?.data?.message || 'Erro ao carregar documentos. Tente novamente.');
        setDocuments([]);
      }
    };

    fetchDocuments();
  }, [token, id_user]);

  useEffect(() => {
    const fetchPreviews = async () => {
      let updated = false;
      const newPreviewUrls = { ...previewUrls };
      for (const doc of documents) {
        if (!newPreviewUrls[doc.name]) {
          try {
            const filename = doc.url.split('/').pop() || '';
            const response = await api.get(`/images/${filename}`, {
              responseType: 'blob',
            });
            const blobUrl = URL.createObjectURL(response.data);
            newPreviewUrls[doc.name] = blobUrl;
            updated = true;
          } catch (err) {
            console.error(`Erro ao buscar imagem para ${doc.name}:`, err);
          }
        }
      }
      if (updated) {
        setPreviewUrls(newPreviewUrls);
      }
    };

    if (documents.length > 0) {
      fetchPreviews();
    }

    return () => {
      Object.values(previewUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [documents, previewUrls]);

  const handleFileUpload = async (docType: string, file: File) => {
    setUploading((prev) => ({ ...prev, [docType]: true }));
    setErrorMessage(null);

    try {
      // Upload da imagem
      const formData = new FormData();
      formData.append('image', file);
      const uploadRes = await api.post('/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fullUrl = uploadRes.data.url;
      const uploadDate = new Date().toISOString().split('T')[0];

      // Verifica se o documento já existe
      const existingDoc = documents.find((d) => d.name === docType);
      const url = existingDoc ? `/docs/${existingDoc.id_docs}` : '/docs';
      const method = existingDoc ? 'put' : 'post';

      // Salva ou atualiza o documento
      await api({
        method,
        url,
        data: {
          name: docType,
          url: fullUrl,
          upload_date: uploadDate,
          id_user,
        },
      });

      // Atualiza a lista de documentos
      const updated = await api.get(`/docs/user/${id_user}`);
      setDocuments(updated.data.data || []);
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error(`Erro ao enviar ${docType}:`, error.response?.status, error.response?.data);
      setErrorMessage(error.response?.data?.message || `Erro ao enviar ${docType}. Tente novamente.`);
    } finally {
      setUploading((prev) => ({ ...prev, [docType]: false }));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, docType: string) => {
    e.preventDefault();
    setDragOverType(null);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(docType, file);
    }
  };

  if (!token || !id_user) {
    return (
      <>
        <TopBar />
        <PageTransition>
          <div className="documents-wrapper">
            <div className="form-container">
              <h2>Sessão expirada</h2>
              <p>Faça login novamente.</p>
              <Button onClick={() => navigate('/rj')} aria-label="Ir para a página de login">
                Ir para Login
              </Button>
            </div>
          </div>
        </PageTransition>
      </>
    );
  }

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="documents-wrapper">
          <WizardStepper current={5} />
          <div className="form-container">
            <h2>Upload de Documentos</h2>
            <AnimatePresence>
              {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
            </AnimatePresence>
            <div className="grid-container">
              {documentTypes.map((docType) => {
                const existing = documents.find((d) => d.name === docType);
                return (
                  <div
                    className={`upload-box${dragOverType === docType ? ' upload-box--drag' : ''}${existing ? ' upload-box--done' : ''}`}
                    key={docType}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!existing) setDragOverType(docType);
                    }}
                    onDragLeave={() => setDragOverType((prev) => (prev === docType ? null : prev))}
                    onDrop={(e) => !existing && handleDrop(e, docType)}
                  >
                    <h4>{docType}</h4>
                    {docType === 'Contracheque/Holerite' && (
                      <p className="observation-note">O envio deve ser do último contracheque</p>
                    )}
                    {existing ? (
                      previewUrls[docType] ? (
                        <motion.img
                          src={previewUrls[docType]}
                          alt={docType}
                          className="preview-image"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      ) : (
                        <p>Carregando imagem...</p>
                      )
                    ) : (
                      <label className="upload-dropzone">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          disabled={uploading[docType]}
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleFileUpload(docType, e.target.files[0]);
                            }
                          }}
                          aria-label={`Fazer upload do documento ${docType}`}
                        />
                        <span>Arraste o arquivo aqui ou clique para escolher</span>
                      </label>
                    )}
                    {uploading[docType] && (
                      <p className="uploading-status">
                        <Spinner className="upload-spinner" /> Enviando…
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="button-group">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/rj/address')}
                aria-label="Voltar para o formulário de endereço"
              >
                Anterior
              </Button>
              <Button
                onClick={() => navigate('/rj/detection-face')}
                aria-label="Prosseguir para a detecção facial"
              >
                Próximo
              </Button>
            </div>
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default DocumentsForm;